// Effect colours (docs/ART-DIRECTION.md §3, §4): the layer's own live Color per faction role and fixed hue, copied from
// the palette (render/palette.ts). A new palette (a retune, or the player's palette setting) rewrites these instances in
// place, so the systems that redraw from a kept colour reference every frame (flights, trails, rain) follow at once;
// one-shot effects (rings, bursts, labels) keep the hue they were spawned with for their short life.

import { Color } from 'three'
import type { OwnerValue, PlayerId } from '@taxwar/net'
import type { ColourRole } from '@taxwar/tuning'
import { factionRole, type FactionRole, type Palette } from '../render/palette'

/** The palette roles effects use as fixed hues: HUD signals, white-hot flash cores and spark amber. */
const FIXED_HUES = ['warning', 'danger', 'text', 'hot', 'spark'] as const satisfies readonly ColourRole[]

/** Impacts and interceptions use fixed hues: the strike's owner is not always known to the viewer. */
export type FixedColourKey = (typeof FIXED_HUES)[number]
/** 'owner' = the placement owner's faction colour relative to the viewer; the rest are fixed hues. */
export type ColourKey = 'owner' | FixedColourKey

const FACTION_ROLES: readonly FactionRole[] = ['player', 'enemy', 'neutral']

export class VfxPalette {
  private readonly factions: Readonly<Record<FactionRole, Color>> = { player: new Color(), enemy: new Color(), neutral: new Color() }
  private readonly hues: Readonly<Record<FixedColourKey, Color>> = { warning: new Color(), danger: new Color(), text: new Color(), hot: new Color(), spark: new Color() }

  constructor(
    private readonly viewer: PlayerId,
    palette: Palette,
  ) {
    this.setPalette(palette)
  }

  setPalette(palette: Palette): void {
    for (const role of FACTION_ROLES) this.factions[role].copy(palette[role])
    for (const hue of FIXED_HUES) this.hues[hue].copy(palette[hue])
  }

  /** The live colour of `key` for an effect about `owner`: the same instance for the layer's life (never mutate it). */
  colourOf(key: ColourKey, owner: OwnerValue): Color {
    return key === 'owner' ? this.factions[factionRole(owner, this.viewer)] : this.hue(key)
  }

  /** The live colour of fixed hue `key`: the same instance for the layer's life (never mutate it). */
  hue(key: FixedColourKey): Color {
    return this.hues[key]
  }
}
